import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import {
  Box, Typography, Table, TableHead, TableBody, TableCell, TableContainer,
  TableRow, IconButton, Tooltip, Chip, CircularProgress, Card,
  CardContent, Grid, TextField, Button, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, Snackbar,
  Avatar, Stack
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import PersonIcon from "@mui/icons-material/Person";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import LogoutIcon from "@mui/icons-material/Logout";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, Link } from "react-router-dom";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [dialog, setDialog] = useState({ open: false, mode: "create", user: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null });
  const [formData, setFormData] = useState({ usuario: "", senha: "", nome: "" });
  const [formErrors, setFormErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { logout, admin } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      const res = await api.get("/admin/users");
      setUsers(res.data);
    } catch (err) {
      console.error(err);
      if (err.response?.status === 401) {
        logout();
      } else {
        setSnackbar({ open: true, message: "Erro ao carregar usuários", severity: "error" });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (mode, user = null) => {
    if (mode === "create") {
      setFormData({ usuario: "", senha: "", nome: "" });
      setFormErrors({});
    } else {
      setFormData({ usuario: user.usuario, senha: "", nome: user.nome });
      setFormErrors({});
    }
    setShowPassword(false);
    setDialog({ open: true, mode, user });
  };

  const handleCloseDialog = () => {
    setDialog({ open: false, mode: "create", user: null });
    setFormData({ usuario: "", senha: "", nome: "" });
    setFormErrors({});
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.usuario.trim()) {
      errors.usuario = "Usuário é obrigatório";
    } else if (formData.usuario.length < 3) {
      errors.usuario = "Usuário deve ter no mínimo 3 caracteres";
    }
    
    if (!formData.nome.trim()) {
      errors.nome = "Nome é obrigatório";
    }
    
    if (dialog.mode === "create" && !formData.senha) {
      errors.senha = "Senha é obrigatória";
    } else if (formData.senha && formData.senha.length < 6) {
      errors.senha = "Senha deve ter no mínimo 6 caracteres";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    try {
      if (dialog.mode === "create") {
        await api.post("/admin/users", formData);
        setSnackbar({ open: true, message: "Usuário criado com sucesso!", severity: "success" });
      } else {
        const updateData = { nome: formData.nome };
        if (formData.senha) {
          updateData.senha = formData.senha;
        }
        await api.put(`/admin/users/${dialog.user.id}`, updateData);
        setSnackbar({ open: true, message: "Usuário atualizado com sucesso!", severity: "success" });
      }
      handleCloseDialog();
      loadUsers();
    } catch (err) {
      if (err.response?.status === 401) {
        logout();
      } else {
        const message = err.response?.data?.error || "Erro ao salvar usuário";
        setSnackbar({ open: true, message, severity: "error" });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/admin/users/${deleteDialog.user.id}`);
      setSnackbar({ open: true, message: "Usuário deletado com sucesso!", severity: "success" });
      setDeleteDialog({ open: false, user: null });
      loadUsers();
    } catch (err) {
      if (err.response?.status === 401) {
        logout();
      } else {
        const message = err.response?.data?.error || "Erro ao deletar usuário";
        setSnackbar({ open: true, message, severity: "error" });
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Nunca";
    const date = new Date(dateString);
    return date.toLocaleDateString("pt-BR", { 
      day: "2-digit", 
      month: "2-digit", 
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  useEffect(() => {
    loadUsers();
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Box textAlign="center">
          <CircularProgress size={60} />
          <Typography variant="h6" mt={2} color="text.secondary">
            Carregando usuários...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: "bold", color: "#1b4332", mb: 1 }}>
            👥 Gerenciar Usuários Administrativos
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Gerencie os usuários com acesso ao painel {admin && `- Logado como: ${admin.nome}`}
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<ArrowBackIcon />}
            component={Link}
            to="/admin"
            sx={{ minWidth: 120 }}
          >
            Voltar
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog("create")}
            sx={{ minWidth: 150 }}
          >
            Novo Usuário
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<LogoutIcon />}
            onClick={handleLogout}
            sx={{ minWidth: 120 }}
          >
            Sair
          </Button>
        </Stack>
      </Box>

      {/* Tabela de Usuários */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead sx={{ background: "linear-gradient(135deg, #1b4332 0%, #2d6a4f 100%)" }}>
              <TableRow>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Usuário</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Nome</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Criado em</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Último Acesso</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }} align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      Nenhum usuário encontrado
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.id} hover sx={{ "&:hover": { backgroundColor: "#f5f5f5" } }}>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Avatar sx={{ bgcolor: "#1b4332", mr: 2, width: 32, height: 32 }}>
                          {admin?.id === user.id ? (
                            <AdminPanelSettingsIcon />
                          ) : (
                            <PersonIcon />
                          )}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                            {user.usuario}
                          </Typography>
                          {admin?.id === user.id && (
                            <Chip label="Você" size="small" color="primary" sx={{ mt: 0.5, height: 20 }} />
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{user.nome}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {formatDate(user.criado_em)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color={user.ultimo_acesso ? "text.primary" : "text.secondary"}>
                        {formatDate(user.ultimo_acesso)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <Tooltip title="Editar">
                          <IconButton
                            color="primary"
                            size="small"
                            onClick={() => handleOpenDialog("edit", user)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={admin?.id === user.id ? "Você não pode deletar a si mesmo" : "Deletar"}>
                          <span>
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => setDeleteDialog({ open: true, user })}
                              disabled={admin?.id === user.id}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Dialog de Criar/Editar */}
      <Dialog open={dialog.open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialog.mode === "create" ? "Criar Novo Usuário" : "Editar Usuário"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Usuário"
                value={formData.usuario}
                onChange={(e) => setFormData({ ...formData, usuario: e.target.value })}
                error={!!formErrors.usuario}
                helperText={formErrors.usuario}
                disabled={dialog.mode === "edit"}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nome Completo"
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                error={!!formErrors.nome}
                helperText={formErrors.nome}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={dialog.mode === "create" ? "Senha" : "Nova Senha (deixe em branco para manter a atual)"}
                type={showPassword ? "text" : "password"}
                value={formData.senha}
                onChange={(e) => setFormData({ ...formData, senha: e.target.value })}
                error={!!formErrors.senha}
                helperText={formErrors.senha || (dialog.mode === "edit" ? "Deixe em branco para manter a senha atual" : "")}
                required={dialog.mode === "create"}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={20} /> : dialog.mode === "create" ? "Criar" : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Deletar */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, user: null })}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja deletar o usuário <strong>{deleteDialog.user?.usuario}</strong> ({deleteDialog.user?.nome})?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Esta ação não pode ser desfeita!
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, user: null })}>
            Cancelar
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
          >
            Deletar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

